import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import * as inputTypes from "components/Forms/InputTypes";
import Selectbox from "components/Forms/Elements/Selectbox";
import MultiSelectbox from "components/Forms/Elements/MultiSelectbox";
import Textbox from "components/Forms/Elements/Textbox";
import DateRange from "components/Forms/Elements/DateRange";
import Textarea from "components/Forms/Elements/Textarea";
import Checkbox from "components/Forms/Elements/Checkbox";
import Radio from "components/Forms/Elements/Radio";
import Translate from "helpers/Translate";

function Preview({ inputs }) {
  const [previewInputs, setPreviewInputs] = useState(inputs);

  useEffect(() => {
    setPreviewInputs(inputs);
  }, [inputs, previewInputs]);

  const onChange = ({ input, newValue, target = null }) => {
    setPreviewInputs(prevInputsState => {
      prevInputsState.forEach(iterator => {
        switch (iterator.type) {
          case inputTypes.TEXTBOX:
          case inputTypes.NUMERIC:
          case inputTypes.EMAIL:
          case inputTypes.TEXTAREA:
          case inputTypes.DATESOLO:
            if (
              iterator.uuidContainer &&
              iterator.uuidContainer === input.uuidContainer
            ) {
              iterator.value = newValue;
            }
            break;
          case inputTypes.DATERANGE:
            if (
              iterator.uuidContainer &&
              iterator.uuidContainer === input.uuidContainer
            ) {
              iterator.value = { ...iterator.value, ...newValue };
            }
            break;
          case inputTypes.CHECKBOX:
            if (iterator.uuidChild && iterator.uuidChild === input.uuidChild) {
              if (target.checked) {
                if (Array.isArray(iterator.value)) {
                  iterator.value = [...iterator.value, newValue];
                } else {
                  iterator.value = [iterator.value, newValue];
                }
              } else {
                iterator.value = iterator.value.filter(i => i !== newValue);
              }
            }
            break;
          case inputTypes.RADIOBOX:
          case inputTypes.SELECT:
            if (iterator.uuidChild && iterator.uuidChild === input.uuidChild) {
              iterator.value = newValue;
            }
            break;
          case inputTypes.MULTISELECT:
            if (iterator.uuidChild && iterator.uuidChild === input.uuidChild) {
              iterator.value = [...newValue];
            }
            break;
          default:
            break;
        }

        if (
          iterator.parent &&
          iterator.parent.uuidContainer === input.uuidContainer
        ) {
          iterator.visible = false;
          if (input.value.includes(iterator.parent.value)) {
            iterator.visible = true;
          }
        }
      });
      return [...prevInputsState];
    });
  };

  const renderPreview = () => {
    if (inputs.length === 0) {
      return (
        <div className="my-30">
          {Translate({
            id:
              "you_have_not_yet_added_any_fields_for_this_metadata_start_adding_by_clicking_on_the_add_field_button"
          })}
        </div>
      );
    }

    return inputs
      .sort((a, b) => a.order - b.order)
      .map((input, idx) => {
        switch (input.type) {
          case inputTypes.TEXTAREA:
            return (
              <div
                key={idx}
                className={classNames("form-group", {
                  hide: !input.visible
                })}
              >
                <label>
                  {input.mandatory && <span className="mr-5">*</span>}{" "}
                  {input.label}
                  <Textarea
                    id={input.name}
                    name={input.name}
                    errors={{}}
                    errorKey={input.name}
                    onReturnKey={() => {}}
                    fieldValue={input.value}
                    onChange={e => {
                      onChange({
                        input,
                        newValue: e.target.value
                      });
                    }}
                  />
                </label>
              </div>
            );
          case inputTypes.DATERANGE:
            return (
              <div
                key={idx}
                className={classNames("form-group", {
                  hide: !input.visible
                })}
              >
                <label>
                  {input.mandatory && <span className="mr-5">*</span>}{" "}
                  {input.label}
                  <DateRange
                    id={input.name}
                    name={input.name}
                    errors={{}}
                    errorKey={input.name}
                    onReturnKey={() => {}}
                    fieldValue={input.value}
                    onChange={rangeObject => {
                      onChange({
                        input,
                        newValue: {
                          ...input.value,
                          [rangeObject.name]: rangeObject.value
                        }
                      });
                    }}
                  />
                </label>
              </div>
            );
          case inputTypes.TEXTBOX:
          case inputTypes.DATESOLO:
          case inputTypes.EMAIL:
          case inputTypes.NUMERIC:
            return (
              <div
                key={idx}
                className={classNames("form-group", {
                  hide: !input.visible
                })}
              >
                <label>
                  {input.mandatory && <span className="mr-5">*</span>}
                  {input.label}
                  <Textbox
                    id={input.name}
                    type={input.type}
                    name={input.name}
                    errorKey={input.name}
                    fieldValue={input.value}
                    mandatory={input.mandatory}
                    errors={{}}
                    onReturnKey={() => {}}
                    onChange={e => {
                      onChange({
                        input,
                        newValue: e.target.value
                      });
                    }}
                  />
                </label>
              </div>
            );
          case inputTypes.CHECKBOX:
            return (
              <div
                key={idx}
                className={classNames("form-group", {
                  hide: !input.visible
                })}
              >
                <label>
                  {input.mandatory && <span className="mr-5">*</span>}{" "}
                  {input.label}
                  <Checkbox
                    id={input.name}
                    name={input.name}
                    direction={input.direction}
                    options={input.options}
                    uuidSelectedOptions={input.uuidSelectedOptions}
                    defaultSelectedValues={input.defaultSelectedValues}
                    errors={{}}
                    errorKey={input.name}
                    onReturnKey={() => {}}
                    fieldValues={
                      Array.isArray(input.value) ? input.value : [input.value]
                    }
                    onChange={e => {
                      onChange({
                        input,
                        newValue: e.target.value,
                        target: e.target
                      });
                    }}
                  />
                </label>
              </div>
            );
          case inputTypes.RADIOBOX:
            return (
              <div
                key={idx}
                className={classNames("form-group", {
                  hide: !input.visible
                })}
              >
                <label>
                  {input.mandatory && <span className="mr-5">*</span>}{" "}
                  {input.label}
                  <Radio
                    id={input.name}
                    name={input.name}
                    direction={input.direction}
                    options={input.options}
                    uuidSelectedOptions={input.uuidSelectedOptions}
                    defaultSelectedValues={input.defaultSelectedValues}
                    errors={{}}
                    errorKey={input.name}
                    onReturnKey={() => {}}
                    fieldValue={input.value}
                    onChange={e => {
                      onChange({
                        input,
                        newValue: e.target.value
                      });
                    }}
                  />
                </label>
              </div>
            );
          case inputTypes.MULTISELECT:
            return (
              <div
                key={idx}
                className={classNames("form-group", {
                  hide: !input.visible
                })}
              >
                <label>
                  {input.mandatory && <span className="mr-5">*</span>}{" "}
                  {input.label}
                  <MultiSelectbox
                    id={input.name}
                    type={input.type}
                    name={input.name}
                    options={input.options}
                    errors={{}}
                    errorKey={input.name}
                    onReturnKey={() => {}}
                    fieldValues={
                      Array.isArray(input.value) ? input.value : [input.value]
                    }
                    onChange={selectedValues => {
                      onChange({
                        input,
                        newValue: selectedValues
                      });
                    }}
                  />
                </label>
              </div>
            );
          case inputTypes.SELECT:
            return (
              <div
                key={idx}
                className={classNames("form-group", {
                  hide: !input.visible
                })}
              >
                <label>
                  {input.mandatory && <span className="mr-5">*</span>}{" "}
                  {input.label}
                  <Selectbox
                    id={input.name}
                    type={input.type}
                    name={input.name}
                    options={input.options}
                    errors={{}}
                    errorKey={input.name}
                    onReturnKey={() => {}}
                    fieldValue={input.value}
                    onChange={e => {
                      onChange({
                        input,
                        newValue: e.target.value
                      });
                    }}
                  />
                </label>
              </div>
            );
          default:
            return null;
        }
      });
  };

  return <div>{renderPreview()}</div>;
}

Preview.propTypes = {
  inputs: PropTypes.array.isRequired
};

export default Preview;
